/**
 * Small meaningful tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_meaning`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const smallMeaningfulTattoosPack: ToilePack = {
    slug: 'small-meaningful-tattoos',
    category: 'tattoo',
    name: 'Small meaningful tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'semicolon', label: 'Semicolon', subject: 'a semicolon whose dot is the body of a tiny butterfly, the sign for a story that continues'},
        {key: 'lotus', label: 'Lotus', subject: 'a small lotus flower rising from a single wave line, standing for growth out of hard times'},
        {key: 'arrow', label: 'Arrow', subject: 'a single arrow pulled back on a simple bow, ready to fly forward'},
    ],
    styles: [
        {key: 'oneline', label: 'Single line', style: 'a minimalist tattoo drawing in one continuous unbroken line of even weight, no fills and no shading', palette: 'black ink only', framing: 'The design, with every piece clearly separate, centered on the sheet with a wide white margin on every side'},
        {key: 'fineline', label: 'Fine line', style: 'a fine-line tattoo drawing, single-needle hairline strokes of even thin weight, delicate and light, no shading', palette: 'black ink only', framing: 'The design, with every piece clearly separate, centered on the sheet with a wide white margin on every side'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, the form built from fine black dots, dot gradients for shade and no continuous outline', palette: 'black ink only', framing: 'The design, with every piece clearly separate, centered on the sheet with a wide white margin on every side'},
    ],
    directions: ['red ink', 'wrist', 'a fox'],
    directionHint: 'try: red ink',
    seo: {
        title: 'Small meaningful tattoos: semicolon, lotus, drawn arrow',
        metaDescription: 'Nine small tattoo designs with meaning: a semicolon with a butterfly, a lotus, a drawn-back arrow, in three styles.',
        h1: 'Small meaningful tattoos: semicolon, lotus, drawn arrow',
        tagline: 'Three small symbols, each in single line, fine line and dotwork.',
        intro: 'A first tattoo is often small and chosen for what it stands for: hope, strength, a new start. The sheet shows a semicolon whose dot is a small butterfly, a lotus rising from a single wave line, and an arrow drawn back on its bow, each in single line, fine line and dotwork. Type a different symbol, such as a fox, and the same three styles carry it.',
        useCases: [
            'a first small tattoo on the wrist',
            'a symbol to show the artist before booking',
            'a small print to live with the design for a while',
        ],
        faq: [
            {q: 'What does each symbol stand for?', a: 'The semicolon marks a story that continues, the lotus stands for growth out of a difficult start, and the arrow stands for being ready to move forward.'},
            {q: 'What is the single line style?', a: 'One continuous unbroken line of even weight, with no fills and no shading.'},
            {q: 'Can I type a different symbol?', a: 'Yes. Type the symbol you want and the three styles are applied to it. The result is a request, not a promised image.'},
            {q: 'Are these shown on skin?', a: 'No. Each design is shown alone on white paper.'},
        ],
    },
}
